import { requestConversationStart, type ConversationStart } from '../components/conversation-cards.js';
// ASIS desktop — Conectores view. Master-detail with:
//   - left: list of connectors + "+" to start a draft
//   - right: editor (draft), or selected connector detail (credential,
//     discover, test, activate, disable, delete), or audit panel
//   - top: conversational setup assistant that produces a draft
//
// All requests go through the SessionController's ApiClient so a logout
// or server change invalidates everything. Each in-flight request has
// its own AbortController; switching selection aborts the previous
// detail actions. On destroy every controller is aborted, DOM secrets
// are cleared and no late renders happen.

import { ApiError, RequestAbortError } from '../../api/client.js';
import type {
  ConnectorAuditEvent,
  ConnectorAssistantMessage,
  ConnectorDraft,
  ConnectorKind,
  ConnectorView,
  ConnectorOperation,
} from '../../api/connectors-types.js';
import { h, toast, confirmModal, type View } from '../components/dom.js';

import { renderHeader } from '../components/header.js';
import type { SessionController } from '../../session/controller.js';

type AuthType = 'none' | 'bearer' | 'api-key';
const AUTH_OPTIONS: Array<{ value: AuthType; label: string }> = [
  { value: 'none', label: 'Sin autenticación' },
  { value: 'bearer', label: 'Bearer (Authorization)' },
  { value: 'api-key', label: 'Api-key en cabecera' },
];
const KIND_OPTIONS: Array<{ value: ConnectorKind; label: string }> = [
  { value: 'mcp', label: 'MCP' },
  { value: 'openapi', label: 'OpenAPI' },
  { value: 'rest', label: 'REST manual' },
];
const STATUS_LABEL: Record<ConnectorView['status'], string> = {
  draft: 'Borrador',
  enabled: 'Habilitado',
  disabled: 'Deshabilitado',
};
const MAX_TURNS = 10;
const MAX_BYTES = 8 * 1024;
const FORBIDDEN_HEADERS = new Set(['host','origin','cookie','set-cookie','connection','content-length','transfer-encoding','referer','proxy-authorization','content-type']);

export function createConectoresView(controller: SessionController): View {
  let alive = true;
  const root = h('section', { class: 'view view-conectores', id: 'view-conectores' });
  root.appendChild(renderHeader('conectores'));
  const body = h('div', { class: 'section-body split connector-list-only' });
  root.appendChild(body);

  const api = controller.getApi();
  const shortcut = (label: string, text: ConversationStart, primary = false): HTMLAnchorElement => {
    const link = h('a', { class: primary ? 'btn primary' : 'btn ghost', href: '#/conversar' }, [label]);
    link.addEventListener('click', () => requestConversationStart(text));
    return link;
  };
  root.insertBefore(h('section', { class: 'card connect-hero' }, [
    h('div', { class: 'connect-hero-text' }, [
      h('h2', { class: 'card-title' }, ['Conéctalo hablando con ASIS']),
      h('p', { class: 'card-sub' }, ['Te pide lo justo, comprueba el acceso y te enseña lo que ya puede consultar. Solo lectura: nunca cambia datos ni envía mensajes.']),
    ]),
    h('div', { class: 'connect-hero-actions' }, [shortcut('Conectar mi CRM', 'conecta mi CRM', true), shortcut('Conectar mi correo', 'conecta mi correo'), shortcut('Otra herramienta', 'conecta una herramienta')]),
  ]), body);
  const mailList = h('div', { class: 'card', 'aria-label': 'Correo conectado' });
  const mailAbort = new AbortController();
  void api.request<{connections:{id:string;label:string}[]}>('GET', '/api/mail/connections', undefined, { signal: mailAbort.signal }).then(result => {
    if (!alive || !result.connections.length) return;
    mailList.append(h('h3', {}, ['Correo de solo lectura']));
    for (const connection of result.connections) {
      const row = h('div', { class: 'card' }, [h('p', {}, [connection.label, ' · Conectado · Disponible en tu unidad'])]);
      const remove = h('button', { class: 'btn ghost', type: 'button' }, ['Desconectar correo']);
      remove.addEventListener('click', async () => {
        const confirmed = await confirmModal({ title: 'Desconectar correo', body: 'ASIS dejará de poder consultar este buzón. Tus correos no se modificarán.', confirmLabel: 'Desconectar' });
        if (!confirmed || !alive) return;
        remove.disabled = true;
        try { await api.request('DELETE', `/api/mail/connections/${encodeURIComponent(connection.id)}`, undefined, { signal: mailAbort.signal }); if (alive) row.remove(); }
        catch { if (alive) { remove.disabled = false; row.append(h('p', { class: 'auth-error', role: 'alert' }, ['No se ha podido desconectar. Inténtalo de nuevo.'])); } }
      });
      row.append(remove); mailList.append(row);
    }
    root.insertBefore(mailList, body);
  }).catch(() => { if (alive) { mailList.append(h('p', { class: 'auth-error', role: 'alert' }, ['No se pudo cargar el correo conectado. Vuelve a abrir Conectores para reintentar.'])); root.insertBefore(mailList, body); } });
  const left = h('div', { class: 'col-list connectors-list-col' });
  const right = h('div', { class: 'col-detail connectors-detail-col', hidden: true });
  body.append(left, right);

  // ---- left column ----
  const listHeader = h('div', { class: 'col-list-header' });
  const listTitle = h('h2', { class: 'card-title' }, ['Conexiones']);
  const newBtn = h('button', { class: 'btn ghost small', type: 'button' }, ['Añadir a mano']);
  newBtn.addEventListener('click', () => selectDraft());
  const refreshBtn = h('button', { class: 'btn ghost small', type: 'button', hidden: true }, ['Reintentar']);
  refreshBtn.addEventListener('click', () => { void refreshList(true); });
  listHeader.append(listTitle, newBtn, refreshBtn);
  left.append(listHeader);
  const list = h('ul', { class: 'connector-list', 'aria-label': 'Lista de conexiones' });
  left.appendChild(list);

  // ---- right column ----
  const detailHost = h('div', { class: 'connector-detail-host' });
  right.appendChild(detailHost);

  let connectors: ConnectorView[] = [];
  let selectedId: string | null = null;
  let mode: 'list' | 'draft' | 'detail' | 'audit' = 'list';
  let draft: ConnectorDraft = emptyDraft();
  let draftId: string | null = null;
  let showDraftEditor = false;
  let listAbort: AbortController | null = null;
  let detailAbort: AbortController | null = null;
  let auditAbort: AbortController | null = null;
  let loading = false;
  const chatTurns: ConnectorAssistantMessage[] = [];
  let chatSubmitting = false;
  let chatAbort: AbortController | null = null;
  let draftMissing: string[] = [];
  let discoveryWarning = '';
  let renderCurrentOperations: (() => void) | null = null;
  const testResults = new Map<string, { version: number; result: unknown; auditId: string }>();
  const passwordInputs = new Set<HTMLInputElement>();

  function formatConflict(err: unknown): string | null {
    if (err instanceof ApiError && err.status === 409) {
      refreshBtn.hidden = false;
      refreshBtn.textContent = 'Actualizar';
    }
    return conflictMessage(err);
  }

  function apiMessage(err: unknown): string {
    if (err instanceof ApiError && err.status === 409) {
      refreshBtn.hidden = false;
      refreshBtn.textContent = 'Actualizar';
    }
    return formatApiMessage(err);
  }

  function trackSecret(input: HTMLInputElement): void {
    passwordInputs.add(input);
  }
  function clearSecrets(): void {
    for (const input of passwordInputs) {
      input.value = '';
      input.placeholder = '';
    }
    passwordInputs.clear();
  }

  function emptyDraft(): ConnectorDraft {
    return { name: '', description: '', kind: 'mcp', endpoint: '', authType: 'none' };
  }

  function destroy(): void {
    alive = false;
    mailAbort.abort();

    listAbort?.abort();
    detailAbort?.abort();
    auditAbort?.abort();
    chatAbort?.abort();
    clearSecrets();
    testResults.clear();
    chatTurns.length = 0;
  }

  async function refreshList(refreshDetail = false): Promise<void> {
    if (loading) return;
    loading = true;
    listAbort?.abort();
    listAbort = new AbortController();
    const request = listAbort;
    list.replaceChildren(h('li', { class: 'muted' }, ['Cargando conexiones…']));
    try {
      const r = await api.listConnectors(request.signal);
      if (!alive || request.signal.aborted) return;
      refreshBtn.hidden = true;
      refreshBtn.textContent = 'Reintentar';
      connectors = r.connectors ?? [];
      renderList();
      if (refreshDetail && selectedId) mode = 'detail';
      if (refreshDetail || mode === 'list') renderDetail();
    } catch (err) {
      if (!alive || err instanceof RequestAbortError) return;
      refreshBtn.hidden = false;
      list.replaceChildren(h('li', { class: 'muted' }, [`No se pudo cargar (${apiMessage(err)})`]));
    } finally { loading = false; }
  }

  function renderList(): void {
    list.replaceChildren();
    if (connectors.length === 0) {
      list.appendChild(h('li', { class: 'empty' }, ['Aún no tienes conexiones. Empieza arriba: ASIS te guía.']));
      return;
    }
    for (const c of connectors) {
      const li = h('li', { class: `connector-row connector-status-${c.status}` });
      const a = h('button', { class: 'connector-link', type: 'button' });
      a.setAttribute('aria-current', selectedId === c.id ? 'true' : 'false');
      const main = h('span', { class: 'connector-row-main' });
      const name = h('span', { class: 'connector-name' }, [c.name || 'Sin nombre']);
      const sub = h('span', { class: 'connector-sub' }, [`${c.managedBy ? 'Desde Espacio' : kindLabel(c.kind)} · ${STATUS_LABEL[c.status]}`]);
      main.append(name, sub);
      const tag = h('span', { class: `tag tag-${c.status}` }, [STATUS_LABEL[c.status]]);
      a.append(main, tag);
      a.addEventListener('click', () => selectConnector(c.id));
      li.appendChild(a);
      list.appendChild(li);
    }
  }

  function selectConnector(id: string): void {
    selectedId = id;
    mode = 'detail';
    detailAbort?.abort();
    auditAbort?.abort();
    chatAbort?.abort();
    renderList();
    renderDetail();
  }

  function startQuickDraft(kind: 'openapi' | 'mcp'): void {
    selectDraft();
    draft.kind = kind;
    draft.authType = 'bearer';
    draft.name = kind === 'openapi' ? 'Mi CRM' : 'Mi servidor MCP';
    draft.description = kind === 'openapi' ? 'Consultas de solo lectura del CRM' : 'Herramientas MCP de solo lectura';
    showDraftEditor = true;
    renderDetail();
  }

  function selectDraft(): void {
    selectedId = null;
    draft = emptyDraft();
    draftId = null;
    showDraftEditor = false;
    chatTurns.length = 0;
    draftMissing = [];
    chatAbort?.abort();
    chatSubmitting = false;
    mode = 'draft';
    detailAbort?.abort();
    auditAbort?.abort();
    renderList();
    renderDetail();
  }

  function showAudit(): void {
    mode = 'audit';
    detailAbort?.abort();
    renderList();
    renderDetail();
  }

  async function confirmForDetail(opts: Parameters<typeof confirmModal>[0]): Promise<boolean> {
    const request = detailAbort!;
    if (!alive || request.signal.aborted) return false;
    const pending = confirmModal(opts);
    const dialog = document.querySelector('.modal-overlay:last-child');
    const cancel = () => (dialog?.querySelector('button') as HTMLButtonElement | null)?.click();
    request.signal.addEventListener('abort', cancel, { once: true });
    try { return await pending; } finally { request.signal.removeEventListener('abort', cancel); }
  }

  function renderDetail(): void {
    right.hidden = mode === 'list';
    body.classList.toggle('connector-list-only', mode === 'list');
    detailAbort?.abort();
    detailAbort = new AbortController();
    auditAbort?.abort();
    clearSecrets();
    detailHost.replaceChildren();
    if (mode === 'audit') { void renderAudit(); return; }
    if (mode === 'draft') { renderDraft(); return; }
    if (mode === 'detail' && selectedId) {
      const c = connectors.find(x => x.id === selectedId);
      if (!c) { detailHost.appendChild(h('p', { class: 'muted' }, ['Conexión no encontrada.'])); return; }
      renderConnectorDetail(c);
    } else {
      if (connectors.length) detailHost.append(h('p', { class: 'muted connector-pick' }, ['Elige una conexión para ver qué puede consultar ASIS.']));
    }
  }

  function findConnector(id: string): ConnectorView | null {
    return connectors.find(c => c.id === id) ?? null;
  }

  function renderManagedDetail(c: ConnectorView): void {
    const request = detailAbort!;
    const current = () => alive && !request.signal.aborted;
    const card = h('section', { class: 'card connector-detail' });
    const head = h('div', { class: 'connector-detail-head' });
    head.append(
      h('div', {}, [h('h2', { class: 'card-title' }, [c.name || 'Sin nombre']), h('p', { class: 'card-sub' }, ['Conectada desde Espacio. ASIS la consulta cuando se lo pides.'])]),
      h('span', { class: `tag tag-${c.status}` }, [STATUS_LABEL[c.status]]),
    );
    const facts = h('dl', { class: 'kv' });
    facts.append(kvRow('Destino', hostOf(c.endpoint) || '—'), kvRow('Permiso', 'Solo consulta'));
    const queries = h('ul', { class: 'connector-managed-ops' });
    for (const operation of c.operations.filter(item => item.eligible)) queries.appendChild(h('li', {}, [operation.description || operation.name]));
    const device = h('input', { type: 'checkbox' }) as HTMLInputElement;
    device.checked = c.allowOnDevice;
    const deviceLabel = h('label', { class: 'field inline' });
    deviceLabel.append(device, h('span', {}, ['Permitir consultas desde mi dispositivo']));
    device.addEventListener('change', async () => {
      device.disabled = true;
      try {
        const r = await api.setConnectorDeviceAccess(c.id, { allow: device.checked, expectedVersion: c.version }, request.signal);
        if (!current()) return;
        replaceConnector(r.connector);
        toast(device.checked ? 'Tu dispositivo ya puede consultarla' : 'Tu dispositivo ya no la consulta', 'ok');
      } catch (error) {
        if (!current() || error instanceof RequestAbortError) return;
        device.checked = c.allowOnDevice;
        device.disabled = false;
        toast(formatConflict(error) ?? apiMessage(error), 'error');
      }
    });
    const open = h('button', { type: 'button', class: 'btn ghost' }, ['Abrir Espacio']);
    open.addEventListener('click', () => { window.location.hash = '#/espacio'; });
    const audit = h('button', { type: 'button', class: 'btn ghost' }, ['Ver auditoría']);
    audit.addEventListener('click', () => showAudit());
    card.append(head, facts, h('h3', { class: 'card-title' }, ['Qué puede consultar']), queries, deviceLabel, h('div', { class: 'form-row' }, [open, audit]));
    detailHost.appendChild(card);
  }

  function renderConnectorDetail(c: ConnectorView): void {
    if (c.managedBy) { renderManagedDetail(c); return; }
    const card = h('section', { class: 'card connector-detail' });
    const title = h('div', { class: 'connector-detail-head' });
    const titleText = h('div', {}, [h('h2', { class: 'card-title' }, [c.name || 'Sin nombre']), h('p', { class: 'card-sub' }, [c.description || 'Sin descripción.'])]);
    const status = h('span', { class: `tag tag-${c.status}` }, [STATUS_LABEL[c.status]]);
    title.append(titleText, status);
    card.appendChild(title);

    const progress = h('ol', { class: 'connector-progress', 'aria-label': 'Progreso de conexión' });
    const steps = ['Conexión', 'Credencial', 'Prueba', 'Activación'];
    const completed = [true, c.authType === 'none' || c.credentialConfigured, c.lastTest?.ok === true, c.status === 'enabled'];
    steps.forEach((step, index) => progress.appendChild(h('li', { class: completed[index] ? 'is-complete' : 'is-pending' }, [step])));
    card.appendChild(progress);
    if (discoveryWarning && selectedId === c.id) card.appendChild(h('p', { class: 'auth-error discovery-warning', role: 'alert' }, [`Conexión guardada, pero no se pudieron descubrir operaciones: ${discoveryWarning}`]));
    discoveryWarning = '';
    const facts = h('dl', { class: 'kv' });
    facts.append(
      kvRow('Tipo', kindLabel(c.kind)),
      kvRow('Endpoint', c.endpoint || '—'),
      kvRow('Host destino', hostOf(c.endpoint) || '—'),
      kvRow('Autenticación', authLabel(c.authType) + (c.headerName ? ` (${c.headerName})` : '')),
      kvRow('Credencial', c.credentialConfigured ? 'Configurada (oculta)' : 'No configurada'),
      kvRow('Operaciones aprobadas', c.approvedOperationIds.length ? c.approvedOperationIds.join(', ') : 'Ninguna'),
      kvRow('Permitir en dispositivo', c.allowOnDevice ? 'Sí' : 'No'),
    );
    card.appendChild(facts);

    if (c.lastTest) {
      const t = c.lastTest;
      const txt = `${t.ok ? 'OK' : 'Error'} · operación ${t.operationId} · ${formatDate(t.at)}` + (t.error ? ` · ${t.error}` : '');
      const last = h('p', { class: 'card-sub' }, [`Última prueba: ${txt}`]);
      card.appendChild(last);
    }

    card.appendChild(renderCredentialBlock(c));
    card.appendChild(renderOperationsBlock(c));
    card.appendChild(renderActionsBlock(c));
    detailHost.appendChild(card);
  }

  function renderCredentialBlock(c: ConnectorView): HTMLElement {
    const request = detailAbort!;
    const current = () => alive && !request.signal.aborted;
    const card = h('section', { class: 'card subcard credential-card' });
    card.append(h('h3', { class: 'card-title' }, ['Credencial']));
    const form = h('form', { class: 'form-stack credential-form' });
    const field = h('label', { class: 'field' });
    field.append(h('span', {}, [c.authType === 'api-key' ? (c.headerName || 'Cabecera') : c.authType === 'bearer' ? 'Clave de acceso' : 'No requiere']));
    const input = h('input', { class: 'input', type: 'password', autocomplete: 'off', spellcheck: 'false', 'aria-label': 'Valor de la credencial', placeholder: c.authType === 'none' ? 'No requiere' : '' });
    if (c.authType === 'none') input.disabled = true;
    field.appendChild(input);
    trackSecret(input);
    form.appendChild(field);
    const actions = h('div', { class: 'form-row' });
    const saveBtn = h('button', { type: 'submit', class: 'btn primary' }, ['Guardar credencial']);
    const clearBtn = h('button', { type: 'button', class: 'btn ghost' }, ['Quitar credencial']);
    saveBtn.disabled = c.authType === 'none';
    clearBtn.disabled = c.authType === 'none' || !c.credentialConfigured;
    actions.append(saveBtn, clearBtn);
    form.appendChild(actions);
    const note = h('p', { class: 'card-sub' }, ['La credencial se cifra en el servidor. La credencial no se incluye en el chat.']);
    form.appendChild(note);
    card.appendChild(form);
    const error = h('p', { class: 'auth-error', role: 'alert' });
    card.appendChild(error);

    form.addEventListener('submit', async e => {
      e.preventDefault();
      if (saveBtn.disabled) return;
      const value = input.value;
      if (c.authType !== 'none' && value.length === 0) { error.textContent = 'Introduce un valor antes de guardar.'; return; }
      if (value.length > 4096) { error.textContent = 'La credencial es demasiado larga (máx. 4096 caracteres).'; return; }
      saveBtn.disabled = clearBtn.disabled = true;
      try {
        const r = await api.setConnectorCredential(c.id, { value, expectedVersion: c.version }, request.signal);
        if (!current()) return;
        input.value = '';
        input.placeholder = '';
        input.dispatchEvent(new Event('blur'));
        replaceConnector(r.connector);
        toast('Credencial guardada', 'ok');
        error.textContent = '';
      } catch (err) {
        if (!current() || err instanceof RequestAbortError) return;
        error.textContent = formatConflict(err) ?? apiMessage(err);
      } finally {
        saveBtn.disabled = c.authType === 'none';
        clearBtn.disabled = c.authType === 'none' || !c.credentialConfigured;
      }
    });

    clearBtn.addEventListener('click', async () => {
      if (clearBtn.disabled) return;
      if (!c.credentialConfigured) { error.textContent = 'No hay credencial para quitar.'; return; }
      clearBtn.disabled = true; saveBtn.disabled = true;
      try {
        const r = await api.setConnectorCredential(c.id, { value: '', expectedVersion: c.version }, request.signal);
        if (!current()) return;
        input.value = '';
        replaceConnector(r.connector);
        toast('Credencial eliminada', 'ok');
      } catch (err) {
        if (!current() || err instanceof RequestAbortError) return;
        error.textContent = formatConflict(err) ?? apiMessage(err);
      } finally { saveBtn.disabled = c.authType === 'none'; clearBtn.disabled = c.authType === 'none' || !c.credentialConfigured; }
    });

    return card;
  }

  function renderOperationsBlock(c: ConnectorView): HTMLElement {
    const request = detailAbort!;
    const current = () => alive && !request.signal.aborted;
    const card = h('section', { class: 'card subcard operations-card' });
    card.append(h('h3', { class: 'card-title' }, ['Operaciones']));
    const controls = h('div', { class: 'form-row' });
    const discoverBtn = h('button', { type: 'button', class: 'btn primary' }, ['Descubrir operaciones']);
    controls.appendChild(discoverBtn);
    card.appendChild(controls);
    const opNote = h('p', { class: 'card-sub' }, [discoverHint(c.kind)]);
    card.appendChild(opNote);
    const opList = h('ul', { class: 'op-list' });
    const opError = h('p', { class: 'auth-error', role: 'alert' });

    if (c.operations.length === 0) {
      opList.appendChild(h('li', { class: 'empty' }, ['Aún no se han descubierto operaciones.']));
    } else {
      for (const op of c.operations) {
        opList.appendChild(renderOperationRow(op));
      }
    }
    card.append(opList, opError);
    const testCard = renderTestBlock(c);
    const activateCard = renderActivateBlock(c);
    card.append(testCard, activateCard);

    discoverBtn.addEventListener('click', async () => {
      if (discoverBtn.disabled) return;
      discoverBtn.disabled = true;
      opError.textContent = '';
      try {
        const r = await api.discoverConnector(c.id, request.signal);
        if (!current()) return;
        replaceConnector(r.connector);
        toast('Operaciones actualizadas', 'ok');
      } catch (err) {
        if (!current() || err instanceof RequestAbortError) return;
        opError.textContent = formatConflict(err) ?? apiMessage(err);
      } finally { discoverBtn.disabled = false; }
    });

    return card;
  }

  function renderOperationRow(op: ConnectorOperation): HTMLElement {
    const li = h('li', { class: `op-row op-${op.eligible ? 'eligible' : 'blocked'}` });
    const main = h('div', { class: 'op-main' });
    const name = h('div', { class: 'op-name' }, [op.name]);
    const desc = h('div', { class: 'op-desc' }, [op.description || 'Sin descripción.']);
    main.append(name, desc);
    const tag = h('span', { class: `tag ${op.eligible ? 'tag-eligible' : 'tag-blocked'}` }, [op.eligible ? 'Disponible' : 'Bloqueada']);
    li.append(main, tag);
    if (!op.eligible && op.blockedReason) {
      const reason = h('div', { class: 'op-blocked' }, [op.blockedReason]);
      li.appendChild(reason);
    }
    if (op.method && op.path) {
      const meta = h('div', { class: 'op-meta' }, [`${op.method} ${op.path}`]);
      li.appendChild(meta);
    }
    return li;
  }

  function renderTestBlock(c: ConnectorView): HTMLElement {
    const request = detailAbort!;
    const current = () => alive && !request.signal.aborted;
    const card = h('section', { class: 'card subcard test-card' });
    card.append(h('h3', { class: 'card-title' }, ['Probar operación']));
    const form = h('form', { class: 'form-stack' });
    const opField = h('label', { class: 'field' });
    opField.append(h('span', {}, ['Operación elegible']));
    const opSelect = h('select', { class: 'select', 'aria-label': 'Operación a probar' });
    const eligible = c.operations.filter(o => o.eligible);
    if (eligible.length === 0) {
      const opt = h('option', { value: '' }, ['Ninguna operación elegible disponible']);
      opt.disabled = true; opt.selected = true;
      opSelect.appendChild(opt);
    } else {
      for (const op of eligible) {
        opSelect.appendChild(h('option', { value: op.id }, [op.name]));
      }
    }
    opField.appendChild(opSelect);
    form.appendChild(opField);
    const argsField = h('label', { class: 'field' });
    argsField.append(h('span', {}, ['Argumentos (JSON)']));
    const argsArea = h('textarea', { class: 'textarea', rows: '5', placeholder: '{\n  "id": "123"\n}', 'aria-label': 'Argumentos JSON' });
    argsField.appendChild(argsArea);
    form.appendChild(argsField);
    const actions = h('div', { class: 'form-row' });
    const testBtn = h('button', { type: 'submit', class: 'btn primary' }, ['Probar']);
    if (eligible.length === 0) testBtn.disabled = true;
    actions.appendChild(testBtn);
    form.appendChild(actions);
    const err = h('p', { class: 'auth-error', role: 'alert' });
    form.appendChild(err);
    card.appendChild(form);
    const result = h('pre', { class: 'result-pre', 'aria-live': 'polite' });
    const previous = testResults.get(c.id);
    result.textContent = previous?.version === c.version ? JSON.stringify(previous.result, null, 2) : 'Sin resultados todavía.';
    card.appendChild(result);
    if (previous?.version === c.version) card.appendChild(h('p', { class: 'card-sub', 'data-audit-id': previous.auditId }, [`Consulta registrada: ${previous.auditId}`]));
    card.dataset.testCard = 'true';

    form.addEventListener('submit', async e => {
      e.preventDefault();
      if (testBtn.disabled) return;
      const opId = opSelect.value;
      if (!opId) { err.textContent = 'Selecciona una operación elegible.'; return; }
      let parsed: Record<string, unknown>;
      try {
        parsed = argsArea.value.trim() === '' ? {} : JSON.parse(argsArea.value) as Record<string, unknown>;
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('El objeto de argumentos debe ser un objeto JSON.');
      } catch (e) {
        err.textContent = `JSON inválido: ${(e as Error).message}`;
        return;
      }
      testBtn.disabled = true;
      err.textContent = '';
      try {
        const r = await api.testConnector(c.id, { operationId: opId, args: parsed, expectedVersion: c.version }, request.signal);
        if (!current()) return;
        testResults.set(c.id, { version: r.connector.version, result: r.result, auditId: r.auditId });
        replaceConnector(r.connector);
        toast(`Prueba correcta (audit ${r.auditId})`, 'ok');
      } catch (error) {
        if (!current() || error instanceof RequestAbortError) return;
        if (error instanceof ApiError) {
          err.textContent = formatConflict(error) ?? error.message;
        } else {
          err.textContent = apiMessage(error);
        }
      } finally { testBtn.disabled = eligible.length === 0; }
    });

    return card;
  }

  function renderActivateBlock(c: ConnectorView): HTMLElement {
    const request = detailAbort!;
    const current = () => alive && !request.signal.aborted;
    const card = h('section', { class: 'card subcard activate-card' });
    card.append(h('h3', { class: 'card-title' }, ['Activar']));
    const note = h('p', { class: 'card-sub' }, ['Marca las operaciones que ASIS podrá llamar y, si quieres, permite también las consultas desde tu dispositivo.']);
    card.appendChild(note);
    const form = h('form', { class: 'form-stack' });
    const eligible = c.operations.filter(o => o.eligible);
    const opBox = h('div', { class: 'checkbox-list' });
    const checks: HTMLInputElement[] = [];
    for (const op of eligible) {
      const lbl = h('label', { class: 'field inline' });
      const cb = h('input', { type: 'checkbox', value: op.id });
      cb.checked = c.approvedOperationIds.includes(op.id);
      lbl.append(cb, h('span', {}, [op.name]));
      opBox.appendChild(lbl);
      checks.push(cb);
    }
    if (eligible.length === 0) opBox.appendChild(h('p', { class: 'muted' }, ['Sin operaciones elegibles.']));
    form.appendChild(opBox);
    const deviceLabel = h('label', { class: 'field inline' });
    const deviceCheck = h('input', { type: 'checkbox' });
    deviceCheck.checked = false;
    deviceLabel.append(deviceCheck, h('span', {}, ['Permitir consultas desde mi dispositivo']));
    form.appendChild(deviceLabel);
    const actions = h('div', { class: 'form-row' });
    const activateBtn = h('button', { type: 'submit', class: 'btn primary' }, ['Activar selección']);
    const updateActivation = () => { activateBtn.disabled = c.lastTest?.ok !== true || !checks.some(check => check.checked); };
    for (const check of checks) check.addEventListener('change', updateActivation);
    updateActivation();
    if (!c.lastTest?.ok) card.appendChild(h('p', { class: 'card-sub' }, ['Primero completa una prueba correcta de esta versión.']));
    actions.append(activateBtn);
    form.appendChild(actions);
    const err = h('p', { class: 'auth-error', role: 'alert' });
    form.appendChild(err);
    card.appendChild(form);

    form.addEventListener('submit', async e => {
      e.preventDefault();
      if (activateBtn.disabled) return;
      const opIds = checks.filter(x => x.checked).map(x => x.value);
      if (opIds.length === 0) { err.textContent = 'Selecciona al menos una operación.'; return; }
      const allowOnDevice = deviceCheck.checked;
      const ok = await confirmForDetail({
        title: 'Activar conexión',
        body: `Vas a activar la conexión «${c.name}» con las operaciones: ${opIds.join(', ')} hacia ${hostOf(c.endpoint) || 'el host declarado'}. El dispositivo ${allowOnDevice ? 'sí' : 'no'} podrá consultarla.`,
        confirmLabel: 'Activar',
        danger: false,
      });
      if (!ok || !current()) return;
      activateBtn.disabled = true;
      try {
        const r = await api.activateConnector(c.id, { operationIds: opIds, allowOnDevice, expectedVersion: c.version, confirm: true }, request.signal);
        if (!current()) return;
        replaceConnector(r.connector);
        toast('Conexión activada', 'ok');
      } catch (error) {
        if (!current() || error instanceof RequestAbortError) return;
        if (error instanceof ApiError) {
          err.textContent = formatConflict(error) ?? error.message;
        } else { err.textContent = apiMessage(error); }
      } finally { updateActivation(); }
    });

    return card;
  }

  function renderActionsBlock(c: ConnectorView): HTMLElement {
    const request = detailAbort!;
    const current = () => alive && !request.signal.aborted;
    const card = h('section', { class: 'card subcard actions-card' });
    card.append(h('h3', { class: 'card-title' }, ['Acciones']));
    const row = h('div', { class: 'form-row' });
    const disableBtn = h('button', { type: 'button', class: 'btn ghost' }, ['Deshabilitar']);
    if (c.status !== 'enabled') disableBtn.disabled = true;
    disableBtn.addEventListener('click', async () => {
      if (disableBtn.disabled) return;
      const ok = await confirmForDetail({
        title: 'Deshabilitar conexión',
        body: `Vas a deshabilitar «${c.name}» (id ${c.id}). Las operaciones aprobadas dejarán de estar disponibles hasta que vuelvas a activarla.`,
        confirmLabel: 'Deshabilitar', danger: true,
      });
      if (!ok || !current()) return;
      disableBtn.disabled = true;
      try {
        const r = await api.disableConnector(c.id, request.signal);
        if (!current()) return;
        replaceConnector(r.connector);
        toast('Conexión deshabilitada', 'ok');
      } catch (err) {
        if (!current() || err instanceof RequestAbortError) return;
        if (err instanceof ApiError) err.message = formatConflict(err) ?? err.message;
        toast(apiMessage(err), 'error');
      } finally { disableBtn.disabled = c.status !== 'enabled'; }
    });
    const delBtn = h('button', { type: 'button', class: 'btn ghost danger' }, ['Eliminar']);
    delBtn.addEventListener('click', async () => {
      const ok = await confirmForDetail({
        title: 'Eliminar conexión',
        body: `Vas a eliminar la conexión «${c.name}» (id ${c.id}). Esta acción no se puede deshacer.`,
        confirmLabel: 'Eliminar', danger: true,
      });
      if (!ok || !current()) return;
      delBtn.disabled = true;
      try {
        await api.deleteConnector(c.id, request.signal);
        if (!current()) return;
        connectors = connectors.filter(x => x.id !== c.id);
        selectedId = null;
        mode = 'list';
        renderList();
        renderDetail();
        toast('Conexión eliminada', 'ok');
      } catch (err) {
        if (!current() || err instanceof RequestAbortError) return;
        toast(formatConflict(err) ?? apiMessage(err), 'error');
      } finally { delBtn.disabled = false; }
    });
    const auditBtn = h('button', { type: 'button', class: 'btn ghost' }, ['Ver auditoría']);
    auditBtn.addEventListener('click', () => showAudit());
    const editBtn = h('button', { type: 'button', class: 'btn ghost' }, ['Editar conexión']);
    editBtn.addEventListener('click', () => {
      draftId = c.id; selectedId = c.id; mode = 'draft';
      draft = { name: c.name, description: c.description, kind: c.kind, endpoint: c.endpoint, authType: c.authType, headerName: c.headerName };
      draftMissing = []; chatAbort?.abort(); renderDetail();
    });
    row.append(editBtn, disableBtn, delBtn, auditBtn);
    card.appendChild(row);
    return card;
  }

  async function renderAudit(): Promise<void> {
    const card = h('section', { class: 'card audit-card' });
    const head = h('div', { class: 'audit-head' });
    head.append(h('h2', { class: 'card-title' }, ['Auditoría de conexiones']));
    const back = h('button', { type: 'button', class: 'btn ghost small' }, ['Volver']);
    back.addEventListener('click', () => { mode = selectedId ? 'detail' : 'list'; renderList(); renderDetail(); });
    head.appendChild(back);
    card.appendChild(head);
    const list = h('ul', { class: 'audit-list' });
    card.appendChild(list);
    detailHost.appendChild(card);
    auditAbort?.abort();
    auditAbort = new AbortController();
    list.appendChild(h('li', { class: 'muted' }, ['Cargando eventos…']));
    try {
      const r = await api.listConnectorAudit(auditAbort.signal);
      if (!alive) return;
      list.replaceChildren();
      const events: ConnectorAuditEvent[] = r.events ?? [];
      if (events.length === 0) { list.appendChild(h('li', { class: 'empty' }, ['Sin eventos registrados.'])); return; }
      for (const e of events) {
        const li = h('li', { class: 'audit-row' });
        const tag = h('span', { class: `tag tag-${e.status}` }, [e.status]);
        const main = h('div', { class: 'audit-main' });
        main.append(
          h('div', { class: 'audit-title' }, [`${e.operationId} · ${e.connectorId}`]),
          h('div', { class: 'audit-sub' }, [`${formatDate(e.at)} · ${e.durationMs} ms`]),
        );
        li.append(tag, main);
        list.appendChild(li);
      }
    } catch (err) {
      if (!alive || err instanceof RequestAbortError) return;
      refreshBtn.hidden = false;
      list.replaceChildren(h('li', { class: 'muted' }, [`No se pudo cargar (${apiMessage(err)})`]));
    }
  }

  function renderDraft(): void {
    const request = detailAbort!;
    const current = () => alive && !request.signal.aborted;
    if (draftId) { detailHost.appendChild(renderDraftEditor()); return; }
    const quickCard = h('section', { class: 'card connector-quick-card' });
    quickCard.append(
      h('h2', { class: 'card-title' }, ['Conecta sin técnicos']),
      h('p', { class: 'card-sub' }, ['Pega la URL y, si hace falta, el token. ASIS guardará la credencial por separado y descubrirá únicamente consultas de lectura.']),
    );
    const quickActions = h('div', { class: 'connector-quick-actions' });
    const quickOpenApi = h('button', { class: 'btn primary', type: 'button' }, ['CRM con URL']);
    quickOpenApi.addEventListener('click', () => startQuickDraft('openapi'));
    const quickMcp = h('button', { class: 'btn ghost', type: 'button' }, ['Servidor MCP']);
    quickMcp.addEventListener('click', () => startQuickDraft('mcp'));
    quickActions.append(quickOpenApi, quickMcp);
    quickCard.appendChild(quickActions);
    detailHost.appendChild(quickCard);
    const chatCard = h('section', { class: 'card chat-card' });
    renderCurrentOperations = null;
    chatCard.append(
      h('h2', { class: 'card-title' }, ['Constructor conversacional']),
      h('p', { class: 'card-sub' }, ['Describe la conexión que quieres añadir (por ejemplo, «quiero consultar las asistencias de mi CRM»). Sólo se envía tu descripción; nunca pegues credenciales aquí.']),
    );
    const history = h('div', { class: 'chat-history', 'aria-live': 'polite' });
    for (const turn of chatTurns) {
      history.appendChild(h('div', { class: `chat-msg chat-${turn.role}` }, [turn.text]));
    }
    if (chatTurns.length === 0) history.appendChild(h('p', { class: 'muted' }, ['Sin mensajes todavía.']));
    chatCard.appendChild(history);
    const ta = h('textarea', { class: 'textarea', rows: '3', placeholder: 'Describe el servicio o escribe el endpoint…', 'aria-label': 'Mensaje para el asistente', maxlength: '4000' });
    const sendBtn = h('button', { type: 'button', class: 'btn primary' }, ['Enviar al asistente']);
    const err = h('p', { class: 'auth-error', role: 'alert' });
    const counter = h('p', { class: 'card-sub' });
    const updateCounter = () => { counter.textContent = `${chatTurns.length} / 10 mensajes`; };
    ta.addEventListener('input', updateCounter);
    updateCounter();
    const skipBtn = h('button', { type: 'button', class: 'btn ghost' }, ['Configurar manualmente']);
    skipBtn.hidden = showDraftEditor;
    skipBtn.addEventListener('click', () => {
      if (!current()) return;
      if (!showDraftEditor) { showDraftEditor = true; detailHost.appendChild(renderDraftEditor()); }
      skipBtn.hidden = true;
      detailHost.querySelector<HTMLInputElement>('[aria-label=Nombre]')?.focus();
    });
    const row = h('div', { class: 'form-row' });
    row.append(sendBtn, skipBtn);
    chatCard.append(ta, row, err, counter);
    detailHost.appendChild(chatCard);

    sendBtn.addEventListener('click', async () => {
      if (chatSubmitting || !current()) return;
      const text = ta.value.trim();
      if (!text) { err.textContent = 'Escribe una descripción antes de enviar.'; return; }
      const next: ConnectorAssistantMessage[] = [...chatTurns, { role: 'user', text }];
      if (next.length >= MAX_TURNS || totalBytes(next) > MAX_BYTES) {
        err.textContent = 'La conversación es demasiado larga. Acorta el mensaje, empieza un nuevo borrador o configura la conexión manualmente.';
        return;
      }
      chatSubmitting = true;
      sendBtn.disabled = true;
      err.textContent = '';
      chatAbort?.abort();
      chatAbort = new AbortController();
      try {
        const r = await api.connectorAssistant({ messages: next }, AbortSignal.any([chatAbort.signal, request.signal]), 25000);
        if (!current()) return;
        chatTurns.length = 0;
        chatTurns.push(...next, { role: 'assistant', text: r.message });
        if (r.draft) { mergeDraft(draft, r.draft); showDraftEditor = true; }
        draftMissing = Array.isArray(r.missing) ? r.missing : [];
        ta.value = '';
        updateCounter();
        renderDetail();
      } catch (e) {
        if (!current() || e instanceof RequestAbortError) return;
        if (e instanceof ApiError) err.textContent = e.message;
        else err.textContent = (e as Error).message;
        // preserve text the user wrote
      } finally { chatSubmitting = false; sendBtn.disabled = false; }
    });

    if (showDraftEditor) detailHost.appendChild(renderDraftEditor());
  }

  function renderDraftEditor(): HTMLElement {
    const request = detailAbort!;
    const current = () => alive && !request.signal.aborted;
    const editing = draftId ? findConnector(draftId) : null;
    const card = h('section', { class: 'card draft-card' });
    card.append(h('h2', { class: 'card-title' }, [editing ? 'Editar conexión' : 'Revisar borrador']), h('p', { class: 'card-sub' }, [editing ? 'Cambiar endpoint o autenticación elimina la credencial y exige descubrir, probar y aprobar de nuevo.' : 'Edita el borrador antes de guardarlo. Los campos obligatorios están marcados.']));
    const creating = !editing;
    const credentialInput = creating ? h('input', { class: 'input', type: 'password', autocomplete: 'new-password', maxlength: '4096', 'aria-label': 'Credencial de conexión' }) : null;
    if (credentialInput) trackSecret(credentialInput);
    const form = h('form', { class: 'form-stack' });
    const name = h('input', { class: 'input', type: 'text', maxlength: '120', 'aria-label': 'Nombre', value: draft.name });
    const description = h('textarea', { class: 'textarea', rows: '2', maxlength: '500', 'aria-label': 'Descripción', value: draft.description });
    description.value = draft.description;
    const kindSel = h('select', { class: 'select', 'aria-label': 'Tipo' });
    for (const opt of KIND_OPTIONS) {
      const o = h('option', { value: opt.value }, [opt.label]);
      if (draft.kind === opt.value) o.selected = true;
      kindSel.appendChild(o);
    }
    kindSel.disabled = Boolean(editing);
    const endpoint = h('input', { class: 'input', type: 'url', placeholder: 'https://…', 'aria-label': 'Endpoint HTTPS', value: draft.endpoint });
    const authSel = h('select', { class: 'select', 'aria-label': 'Autenticación' });
    for (const opt of AUTH_OPTIONS) {
      const o = h('option', { value: opt.value }, [opt.label]);
      if (draft.authType === opt.value) o.selected = true;
      authSel.appendChild(o);
    }
    const headerName = h('input', { class: 'input', type: 'text', placeholder: 'Authorization o x-api-key', 'aria-label': 'Nombre de cabecera', value: draft.headerName ?? '' });

    form.append(
      field('Nombre', name, true),
      field('Descripción', description, true),
      field('Tipo', kindSel, true),
      field('Endpoint HTTPS', endpoint, true),
      field('Autenticación', authSel, true),
      field('Cabecera (sólo api-key)', headerName, false),
      ...(credentialInput ? [field('Credencial (opcional, máx. 4096 caracteres)', credentialInput, false)] : []),
    );
    const endpointHelp = h('p', { class: 'card-sub openapi-endpoint-help' }, ['OpenAPI: si indicas sólo la URL base, se usará /openapi.json. Las rutas explícitas .json, .yaml o .yml se conservan.']);
    endpointHelp.hidden = kindSel.value !== 'openapi';
    form.insertBefore(endpointHelp, form.children[3]!);
    const updateEndpointHelp = () => {
      endpointHelp.hidden = kindSel.value !== 'openapi';
      if (endpointHelp.hidden) return;
      const entered = endpoint.value.trim();
      const normalized = normalizedEndpoint(entered, 'openapi');
      endpointHelp.textContent = entered && normalized !== entered
        ? `Se guardará como ${normalized}`
        : 'Pega una URL base o la URL exacta de su documento OpenAPI.';
    };

    const operationsHost = h('div', { class: 'draft-operations' });

    form.appendChild(operationsHost);
    const missingBox = h('p', { class: 'card-sub' });
    const renderMissing = () => {
      if (draftMissing.length === 0) { missingBox.textContent = ''; return; }
      missingBox.textContent = `Falta por completar: ${draftMissing.join(', ')}`;
    };
    renderMissing();
    form.appendChild(missingBox);
    const renderOps = () => {
      operationsHost.replaceChildren();
      if (editing) return;
      if (kindSel.value !== 'rest') {
        operationsHost.appendChild(h('p', { class: 'card-sub' }, ['Para MCP y OpenAPI las operaciones se descubren tras guardar el borrador.']));
        return;
      }
      operationsHost.appendChild(h('h3', { class: 'card-title' }, ['Operaciones REST']));
      const ops = draft.operations ?? [];
      for (let i = 0; i < ops.length; i++) {
        operationsHost.appendChild(restOperationRow(ops[i]!, i));
      }
      const add = h('button', { type: 'button', class: 'btn ghost small' }, ['Añadir operación GET']);
      add.addEventListener('click', () => {
        draft.operations = [...ops, { name: '', description: '', path: '', inputSchema: { type: 'object', properties: {} } }];
        renderOps();
      });
      operationsHost.appendChild(add);
    };
    renderCurrentOperations = renderOps;
    kindSel.addEventListener('change', () => { draft.kind = kindSel.value as ConnectorKind; updateEndpointHelp(); renderOps(); });
    authSel.addEventListener('change', () => { draft.authType = authSel.value as AuthType; });
    name.addEventListener('input', () => { draft.name = name.value; });
    description.addEventListener('input', () => { draft.description = description.value; });
    endpoint.addEventListener('input', () => { draft.endpoint = endpoint.value; updateEndpointHelp(); });
    headerName.addEventListener('input', () => { draft.headerName = headerName.value; });
    updateEndpointHelp();
    renderOps();

    const err = h('p', { class: 'auth-error', role: 'alert' });
    const actions = h('div', { class: 'form-row' });
    const saveBtn = h('button', { type: 'submit', class: 'btn primary' }, [editing ? 'Guardar cambios' : 'Guardar y descubrir']);
    actions.append(saveBtn);
    form.append(err, actions);
    card.appendChild(form);

    form.addEventListener('submit', async e => {
      e.preventDefault();
      if (saveBtn.disabled) return;
      if (!current() || !form.checkValidity()) return;
      if (credentialInput && credentialInput.value.length > 4096) { err.textContent = 'La credencial es demasiado larga (máx. 4096 caracteres).'; return; }
      const validation = validateDraft(draft, kindSel.value as ConnectorKind, Boolean(editing));
      if (validation) { err.textContent = validation; return; }
      saveBtn.disabled = true;
      try {
        const body: ConnectorDraft = {
          name: draft.name.trim(),
          description: draft.description.trim(),
          kind: kindSel.value as ConnectorKind,
          endpoint: normalizedEndpoint(endpoint.value.trim(), kindSel.value as ConnectorKind),
          authType: authSel.value as AuthType,
        };
        const headerVal = headerName.value.trim();
        if (authSel.value === 'api-key' && headerVal) body.headerName = headerVal;
        if (kindSel.value === 'rest' && draft.operations && draft.operations.length > 0) {
          body.operations = draft.operations;
        }
        const r = editing
          ? await api.patchConnector(editing.id, { name: body.name, description: body.description, endpoint: body.endpoint, authType: body.authType, ...(body.headerName ? { headerName: body.headerName } : {}), expectedVersion: editing.version }, request.signal)
          : await api.createConnector(body, request.signal);
        if (!current()) return;
        draftId = r.connector.id;
        let saved = r.connector;
        connectors = editing ? connectors.map(c => c.id === editing.id ? saved : c) : [saved, ...connectors];
        if (!editing && credentialInput?.value) {
          const secret = credentialInput.value;
          credentialInput.value = '';
          try {
            const credential = await api.setConnectorCredential(saved.id, { value: secret, expectedVersion: saved.version }, request.signal);
            if (!current()) return;
            saved = credential.connector;
            connectors = connectors.map(c => c.id === saved.id ? saved : c);
          } finally { credentialInput.value = ''; }
        }
        if (!editing && (saved.kind === 'openapi' || saved.kind === 'mcp')) {
          try {
            const discovered = await api.discoverConnector(saved.id, request.signal);
            if (!current()) return;
            saved = discovered.connector;
            connectors = connectors.map(c => c.id === saved.id ? saved : c);
          } catch (discoveryError) {
            if (!current() || discoveryError instanceof RequestAbortError) return;
            discoveryWarning = apiMessage(discoveryError);
          }
        }
        toast('Conexión guardada', 'ok');
        selectConnector(saved.id);
      } catch (error) {
        if (!current() || error instanceof RequestAbortError) return;
        if (error instanceof ApiError) err.textContent = formatConflict(error) ?? error.message;
        else err.textContent = (error as Error).message;
      } finally { saveBtn.disabled = false; }
    });

    return card;
  }

  function restOperationRow(op: { name: string; description: string; path: string; inputSchema: Record<string, unknown> }, index: number): HTMLElement {
    const wrap = h('div', { class: 'card subcard op-draft' });
    const head = h('div', { class: 'form-row' });
    head.append(h('h4', { class: 'card-title' }, [`Operación ${index + 1}`]));
    const remove = h('button', { type: 'button', class: 'btn ghost small danger' }, ['Quitar']);
    remove.addEventListener('click', () => {
      const list = draft.operations ?? [];
      list.splice(index, 1);
      draft.operations = list;
      renderCurrentOperations?.();
    });
    head.appendChild(remove);
    wrap.appendChild(head);
    const name = h('input', { class: 'input', type: 'text', placeholder: 'Nombre', 'aria-label': 'Nombre de la operación', value: op.name });
    const desc = h('input', { class: 'input', type: 'text', placeholder: 'Descripción', 'aria-label': 'Descripción', value: op.description });
    const path = h('input', { class: 'input', type: 'text', placeholder: '/ruta', 'aria-label': 'Ruta', value: op.path });
    const advancedBtn = h('button', { type: 'button', class: 'btn ghost small' }, ['Editar inputSchema (JSON)']);
    const advanced = h('textarea', { class: 'textarea', rows: '5', 'aria-label': 'inputSchema JSON' });
    advanced.value = JSON.stringify(op.inputSchema, null, 2);
    advanced.hidden = true;
    advancedBtn.addEventListener('click', () => { advanced.hidden = !advanced.hidden; });
    advanced.addEventListener('input', () => {
      const trimmed = advanced.value.trim();
      if (!trimmed) { advanced.setCustomValidity(''); advanced.value = JSON.stringify({ type: 'object', properties: {} }, null, 2); op.inputSchema = { type: 'object', properties: {} }; return; }
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        const err = validateInputSchema(parsed);
        if (err) { advanced.setCustomValidity(err); advanced.reportValidity(); return; }
        advanced.setCustomValidity('');
        op.inputSchema = parsed as Record<string, unknown>;
      } catch (e) { advanced.setCustomValidity(`JSON inválido: ${(e as Error).message}`); advanced.reportValidity(); }
    });
    name.addEventListener('input', () => { op.name = name.value; });
    desc.addEventListener('input', () => { op.description = desc.value; });
    path.addEventListener('input', () => { op.path = path.value; });
    wrap.append(field('Nombre', name, true), field('Descripción', desc, false), field('Ruta', path, true), advancedBtn, advanced);
    return wrap;
  }

  function replaceConnector(next: ConnectorView): void {
    connectors = connectors.map(c => c.id === next.id ? next : c);
    if (selectedId === next.id) { /* re-render to reflect changes */ renderDetail(); }
    renderList();
  }

  void refreshList();
  return { el: root, destroy() { destroy(); }, update() { void refreshList(); } };
}

function normalizedEndpoint(endpoint: string, kind: ConnectorKind): string {
  if (kind !== 'openapi') return endpoint;
  try {
    const url = new URL(endpoint);
    if (url.pathname === '' || url.pathname === '/') url.pathname = '/openapi.json';
    return url.toString();
  } catch { return endpoint; }
}

function kindLabel(kind: ConnectorKind): string {
  return kind === 'mcp' ? 'MCP' : kind === 'openapi' ? 'OpenAPI' : 'REST';
}

function authLabel(auth: ConnectorView['authType']): string {
  return auth === 'none' ? 'Ninguna' : auth === 'bearer' ? 'Bearer' : 'Api-key';
}

function hostOf(endpoint: string): string | null {
  try { return new URL(endpoint).host; } catch { return null; }
}

function discoverHint(kind: ConnectorKind): string {
  if (kind === 'mcp') return 'MCP se conecta al endpoint y carga las herramientas declaradas. Sólo se aprueban operaciones de lectura.';
  if (kind === 'openapi') return 'OpenAPI importa el documento del endpoint. Sólo se aprueban rutas GET del mismo host.';
  return 'Las consultas REST se definen al crear la conexión. Aquí puedes revisarlas antes de probarlas.';
}

function field(label: string, control: HTMLElement, required: boolean): HTMLElement {
  const wrap = h('label', { class: 'field' });
  wrap.append(h('span', {}, [label + (required ? ' *' : '')]), control);
  return wrap;
}

function kvRow(label: string, value: string): HTMLElement {
  return h('div', { class: 'kv-row' }, [h('dt', {}, [label]), h('dd', {}, [value])]);
}

function validateDraft(draft: ConnectorDraft, kind: ConnectorKind, editing = false): string | null {
  if (!draft.name.trim()) return 'El nombre es obligatorio.';
  if (!draft.description.trim()) return 'La descripción es obligatoria.';
  if (kind === 'mcp' || kind === 'openapi' || kind === 'rest') {
    if (!draft.endpoint.trim()) return 'El endpoint HTTPS es obligatorio.';
    try { const u = new URL(draft.endpoint); if (u.protocol !== 'https:') return 'El endpoint debe ser HTTPS.'; if (u.username || u.password) return 'El endpoint no puede contener credenciales.'; if (u.search || u.hash) return 'El endpoint no puede contener consulta ni fragmento.'; }
    catch { return 'Endpoint no válido.'; }
  }
  if (draft.authType === 'api-key') {
    const h = (draft.headerName ?? '').trim().toLowerCase();
    if (!h) return 'Indica el nombre de la cabecera para api-key.';
    if (!/^[a-z][a-z0-9-]{0,63}$/.test(h) || FORBIDDEN_HEADERS.has(h) || h.startsWith('sec-') || h.startsWith('proxy-')) return 'Usa una cabecera de token válida, como Authorization o x-api-key.';
  }
  if (kind === 'rest' && !editing) {
    const ops = draft.operations ?? [];
    if (ops.length === 0) return 'Define al menos una operación GET para REST.';
    for (const op of ops) {
      if (!op.name.trim()) return 'Todas las operaciones necesitan nombre.';
      if (!op.path.trim() || !op.path.startsWith('/') || op.path.startsWith('//')) return 'La ruta debe comenzar por «/».';
      const err = validateInputSchema(op.inputSchema);
      if (err) return `inputSchema de ${op.name}: ${err}`;
    }
  }
  return null;
}

function validateInputSchema(value: unknown): string | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 'debe ser un objeto JSON.';
  const v = value as Record<string, unknown>;
  if (v.type !== 'object') return 'el campo type debe ser "object".';
  if (v.properties !== undefined && (typeof v.properties !== 'object' || v.properties === null || Array.isArray(v.properties))) return 'properties debe ser un objeto.';
  if (v.required !== undefined) {
    if (!Array.isArray(v.required)) return 'required debe ser una lista de cadenas.';
    for (const r of v.required) if (typeof r !== 'string') return 'required contiene un valor no cadena.';
  }
  if (v.additionalProperties !== undefined && typeof v.additionalProperties !== 'boolean' && typeof v.additionalProperties !== 'object') return 'additionalProperties debe ser booleano u objeto.';
  return null;
}

function totalBytes(turns: ConnectorAssistantMessage[]): number {
  return turns.reduce((acc, t) => acc + new TextEncoder().encode(t.text).length, 0);
}

function formatApiMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return (err as Error).message ?? 'error';
}

function conflictMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status === 409) return 'La versión del servidor ha cambiado. Pulsa Actualizar para recargarla; revisa los cambios antes de volver a guardar.';
  if (err.status === 401) return 'La sesión ha caducado. Vuelve a iniciar sesión.';
  if (err.status === 403) return 'No tienes permiso para esta acción.';
  if (err.status === 413) return 'El servidor rechazó el contenido por ser demasiado grande.';
  return null;
}

function formatDate(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  return new Date(t).toLocaleString();
}

function mergeDraft(target: ConnectorDraft, patch: Partial<ConnectorDraft>): void {
  if (typeof patch.name === 'string') target.name = patch.name;
  if (typeof patch.description === 'string') target.description = patch.description;
  if (patch.kind) target.kind = patch.kind;
  if (typeof patch.endpoint === 'string') target.endpoint = patch.endpoint;
  if (patch.authType) target.authType = patch.authType;
  if (typeof patch.headerName === 'string') target.headerName = patch.headerName;
  if (Array.isArray(patch.operations) && patch.operations.length > 0) {
    target.operations = patch.operations.map(o => ({ ...o }));
  }
}
